import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Car, Home, Package, IndianRupee } from 'lucide-react';
import { fmtINR } from './FinSheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bar = ({ pct, color }) => (
  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

const Metric = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-400 font-medium">{label}</div>
    <div className="text-sm font-bold text-slate-800">{value}</div>
  </div>
);

export const CMDFinancePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/finance/snehal-overview`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm" data-testid="cmd-finance-loading">Loading Finance summary…</div>;
  if (!data) return null;

  const { kokan, rowhouse, marketing } = data;

  return (
    <Card className="border-0 shadow-md" data-testid="cmd-finance-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2">
          <IndianRupee className="w-4 h-4 text-emerald-600" />
          Finance Snapshot — Snehal Katkar (%-wise)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Kokan */}
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid="cmd-finance-kokan">
            <div className="flex items-center gap-2 mb-2"><Car className="w-4 h-4 text-blue-600" /><span className="font-bold text-sm text-blue-900">Kokan Travel</span></div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Metric label="Payable" value={fmtINR(kokan.payable)} />
              <Metric label="Paid" value={fmtINR(kokan.paid)} />
              <Metric label="Received" value={fmtINR(kokan.received)} />
              <Metric label="Bal. Recovery" value={fmtINR(kokan.balanceRecovery)} />
            </div>
            <div className="text-[11px] font-semibold text-blue-800 flex justify-between"><span>Paid</span><span>{kokan.paidPct}%</span></div>
            <Bar pct={kokan.paidPct} color="bg-blue-500" />
            <div className="text-[11px] font-semibold text-blue-800 flex justify-between mt-2"><span>Recovery</span><span>{kokan.recoveryPct}%</span></div>
            <Bar pct={kokan.recoveryPct} color="bg-emerald-500" />
          </div>

          {/* Rowhouse */}
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="cmd-finance-rowhouse">
            <div className="flex items-center gap-2 mb-2"><Home className="w-4 h-4 text-emerald-600" /><span className="font-bold text-sm text-emerald-900">Row House</span></div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Metric label="Receivable" value={fmtINR(rowhouse.receivable)} />
              <Metric label="Received" value={fmtINR(rowhouse.received)} />
              <Metric label="Bal. Recovery" value={fmtINR(rowhouse.balanceRecovery)} />
              <Metric label="Bookings" value={rowhouse.bookings} />
            </div>
            <div className="text-[11px] font-semibold text-emerald-800 flex justify-between"><span>Received</span><span>{rowhouse.receivedPct}%</span></div>
            <Bar pct={rowhouse.receivedPct} color="bg-emerald-500" />
          </div>

          {/* Marketing Kit */}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100" data-testid="cmd-finance-marketing">
            <div className="flex items-center gap-2 mb-2"><Package className="w-4 h-4 text-amber-600" /><span className="font-bold text-sm text-amber-900">Marketing Kit</span></div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Metric label="Kit Amount" value={fmtINR(marketing.amount)} />
              <Metric label="Total Kit Paid" value={fmtINR(marketing.kitPaid)} />
            </div>
            <div className="text-[11px] font-semibold text-amber-800 flex justify-between"><span>Paid</span><span>{marketing.paidPct}%</span></div>
            <Bar pct={marketing.paidPct} color="bg-amber-500" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CMDFinancePanel;
